import React, { useEffect, useState } from 'react';
import { WifiOff, Cloud, CloudOff, CheckCircle2 } from 'lucide-react';
import { useOnline } from '@/lib/useOnline';
import { drainQueue } from '@/lib/offlineSync';
import { listQueue } from '@/lib/offlineDb';
import { useLang } from '@/context/LangContext';
import { useAuth } from '@/context/AuthContext';

/**
 * Banner shown at the very top of every page when:
 *   - The device is offline → red "Sin conexión" pill.
 *   - The device just came back online AND there are queued writes →
 *     amber "Sincronizando" pill that auto-resolves to a green "Listo" toast.
 */
export default function OfflineBanner() {
  const online = useOnline();
  const { offlineMode } = useAuth();
  const { t } = useLang();
  const [pendingCount, setPendingCount] = useState(0);
  const [syncStatus, setSyncStatus] = useState(null); // null | 'syncing' | 'done'

  // Refresh pending count whenever connectivity changes.
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      const items = await listQueue().catch(() => []);
      if (!cancelled) setPendingCount(items.length);
    };
    tick();
    const id = setInterval(tick, 5000);
    return () => { cancelled = true; clearInterval(id); };
  }, [online]);

  // Auto-drain when we come back online.
  useEffect(() => {
    if (!online) return;
    if (pendingCount === 0) return;
    let cancelled = false;
    setSyncStatus('syncing');
    drainQueue().then((r) => {
      if (cancelled) return;
      setSyncStatus('done');
      setPendingCount(r.remaining);
      setTimeout(() => setSyncStatus(null), 3000);
    }).catch(() => {
      if (!cancelled) setSyncStatus(null);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [online]);

  if (online && !offlineMode && syncStatus === null) return null;

  const pillBase = 'fixed top-2 left-1/2 -translate-x-1/2 z-[60] flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-h uppercase tracking-wider shadow-lg backdrop-blur-sm';

  if (!online) {
    return (
      <div className={`${pillBase} bg-red-900/80 border border-red-500/60 text-red-100`} data-testid="offline-banner-offline">
        <WifiOff className="w-3.5 h-3.5" /> {t('offline_no_connection')}
        {pendingCount > 0 && <span className="text-red-200/80">· {pendingCount} {t('offline_pending')}</span>}
      </div>
    );
  }
  if (syncStatus === 'syncing') {
    return (
      <div className={`${pillBase} bg-yellow-900/80 border border-yellow-500/60 text-yellow-100`} data-testid="offline-banner-syncing">
        <Cloud className="w-3.5 h-3.5 animate-pulse" /> {t('offline_syncing')}
      </div>
    );
  }
  if (syncStatus === 'done') {
    return (
      <div className={`${pillBase} bg-emerald-900/80 border border-emerald-500/60 text-emerald-100`} data-testid="offline-banner-synced">
        <CheckCircle2 className="w-3.5 h-3.5" /> {t('offline_synced')}
      </div>
    );
  }
  if (offlineMode) {
    return (
      <div className={`${pillBase} bg-blue-900/80 border border-blue-500/60 text-blue-100`} data-testid="offline-banner-cached">
        <CloudOff className="w-3.5 h-3.5" /> {t('offline_cached_session')}
      </div>
    );
  }
  return null;
}
